import { dayInZone, type AdmissionRequest } from "./arbiter";
import { envValue, type EnvRecord } from "./envPreflight";

/**
 * TA-1371 C5 (CLAUDE.md sections 7, 11 and 0; ManyChat teardown, "a human
 * reply halts automations"). The owner's own reply outranks the day's
 * scheduled plays.
 *
 * Section 7: "active conversation beats scheduled plays". The arbiter applied
 * that only to sends it admitted itself, so an owner answering a customer by
 * hand in the Meta inbox (an echo, packages/db ingest.ts recordEcho) never
 * reached it: a review ask, a missed-call text-back or a booking confirmation
 * could still land hours later the same day, a second voice behind the
 * owner's. With this on, that echo records the shop's day for the contact as
 * an active conversation at the one arbiter, through the store's own
 * `admit`. No new number: the hold is the arbiter's existing day, ending at
 * the shop's midnight, and the turn spends no slot (`countsTowardCap` false,
 * Kabir's typed 2026-10-16 go) and is entitled to none (`maxPerDay` 0).
 *
 * Nothing here sends, and nothing is loosened: an active_conversation
 * admission only ever raises the day's top priority, so it can only withhold.
 *
 * Built switched off (section 11): it changes what reaches a customer. Two
 * reasons it waits for Kabir's typed go, both in FINDINGS:
 *  - a non-ours echo is not always the owner. TA-612: a Page auto-reply or a
 *    third-party tool on the inbox looks exactly like the owner answering, so
 *    on a shop running one this would mark most days active;
 *  - the per-person plays it holds include ones the customer asked for (a
 *    consult booking confirmation, an appointment reminder), withheld for the
 *    rest of a day the owner spoke.
 */

export const ENABLE_OWNER_TURN_PRECEDENCE_ENV = "ACKRA_ENABLE_OWNER_TURN_PRECEDENCE";

/** The alert a failed precedence write raises. Not on the amber or owner lists, so it pages (alerts.ts). */
export const OWNER_TURN_PRECEDENCE_FAILED_ALERT_KIND = "owner_turn_precedence_failed";

/** Null when the value is a valid flag, or the problem sentence. Never quotes the value. */
export function validateEnableOwnerTurnPrecedence(raw: string): string | null {
  if (raw === "0" || raw === "1") return null;
  return 'must be exactly "1" (the owner\'s turn outranks scheduled plays) or "0" (off). Any other value is refused rather than read as on';
}

/** True when the flag, read as set, is exactly "1". */
export function ownerTurnPrecedenceRequested(env: EnvRecord): boolean {
  return envValue(env, ENABLE_OWNER_TURN_PRECEDENCE_ENV) !== undefined && env[ENABLE_OWNER_TURN_PRECEDENCE_ENV] === "1";
}

/** Exactly "1" is on; unset, empty or "0" is off; anything else throws. Default OFF. */
export function planOwnerTurnPrecedence(env: EnvRecord): boolean {
  if (envValue(env, ENABLE_OWNER_TURN_PRECEDENCE_ENV) === undefined) return false;
  const complaint = validateEnableOwnerTurnPrecedence(env[ENABLE_OWNER_TURN_PRECEDENCE_ENV] ?? "");
  if (complaint !== null) throw new Error(`${ENABLE_OWNER_TURN_PRECEDENCE_ENV} ${complaint}.`);
  return env[ENABLE_OWNER_TURN_PRECEDENCE_ENV] === "1";
}

/** The boot line, printed either way. */
export function ownerTurnPrecedenceBannerLine(on: boolean): string {
  return on
    ? "  ownerTurn   ON, an echo not sent by the agent holds the contact's scheduled plays at the arbiter until the shop's midnight"
    : `  ownerTurn   OFF, the owner's hand replies do not reach the arbiter (${ENABLE_OWNER_TURN_PRECEDENCE_ENV} is not 1)`;
}

export interface OwnerTurnInput {
  readonly clientId: string;
  readonly contactId: string;
  /** When the owner's message was sent (the echo's own time, so a late delivery lands on its real day). */
  readonly at: Date;
  /** `clients.timezone`. Null is "not set", read as UTC exactly as `SendArbiter` reads an absent zone. */
  readonly timeZone: string | null;
}

/**
 * The admission an owner turn writes. Throws on an invalid time or a zone this
 * runtime does not know, never a guessed day: the caller turns the throw into
 * a page (section 0).
 */
export function ownerTurnAdmission(input: OwnerTurnInput): AdmissionRequest {
  if (Number.isNaN(input.at.getTime())) {
    throw new Error("ownerTurnAdmission: the owner's turn has an invalid time");
  }
  const zone = input.timeZone ?? "UTC";
  let day: string;
  try {
    day = dayInZone(input.at, zone);
  } catch {
    throw new Error(
      `ownerTurnAdmission: shop time zone ${JSON.stringify(zone.slice(0, 64))} is not a time zone this runtime knows; fix clients.timezone`,
    );
  }
  return {
    clientId: input.clientId,
    contactId: input.contactId,
    day,
    priority: "active_conversation",
    maxPerDay: 0,
    countsTowardCap: false,
  };
}
